<!-- eslint-disable vue/no-unused-vars -->
<template>
  <div class="modern-dashboard">
    <!-- Top Header with Brand Identity -->
    <div class="dashboard-header">
      <div class="header-content">
        <div class="brand-section">
          <div class="brand-icon">
            <feather-icon
              icon="TruckIcon"
              size="32"
              class="brand-logo"
            />
          </div>
          <div class="brand-info">
            <h1 class="brand-title">
              Bienvenue, {{ user.username }} 👋
            </h1>
            <p class="brand-subtitle">
              Voici un aperçu de vos performances aujourd'hui
            </p>
          </div>
        </div>

        <div class="header-actions">
          <div class="time-display">
            <feather-icon
              icon="ClockIcon"
              size="16"
              class="time-icon"
            />
            <span class="current-time">{{ getCurrentTime() }}</span>
          </div>
          <b-button
            variant="outline-light"
            size="sm"
            class="refresh-btn"
            :disabled="isLoading"
            @click="refreshData"
          >
            <feather-icon
              :icon="isLoading ? 'RefreshCwIcon' : 'RefreshCwIcon'"
              size="16"
              :class="{ 'spinning': isLoading }"
            />
            {{ isLoading ? 'Chargement...' : 'Actualiser' }}
          </b-button>
        </div>
      </div>
    </div>

    <!-- Info Notice Section -->
    <div class="info-notice-container">
      <div class="info-notice">
        <div class="notice-icon">
          <feather-icon
            icon="InfoIcon"
            size="20"
            class="icon"
          />
        </div>
        <div class="notice-content">
          <p class="notice-text">
            حرصا منا على توفير المعلوم المتفق عليه في الأجل المحدد ، نعلم حرفاءنا الكرام أنه سيتم اقتطاع 1د كمعلوم بسيط على كل فاتورة خلاص كما نعدكم بمزيد التألق والنجاح.
          </p>
          <p
            v-if="$branding.features.showSlogan && $branding.company.slogan"
            class="notice-tagline"
          >
            {{ $branding.company.shortName }} : {{ $branding.company.slogan }}
          </p>
        </div>
      </div>
    </div>

    <br>

    <!-- Loading Overlay -->
    <div
      v-if="isLoading"
      class="loading-overlay"
    >
      <div class="loading-content">
        <div class="loading-spinner" />
        <p class="loading-text">
          Chargement des statistiques...
        </p>
      </div>
    </div>

    <!-- Statistics Grid -->
    <div class="stats-container">
      <div
        v-if="todayStatusConfigs.length"
        class="today-stats-grid"
      >
        <div
          v-for="statusConfig in todayStatusConfigs"
          :key="`today-${statusConfig.status}`"
          class="today-stat-card"
          @click="show(statusConfig.status)"
        >
          <div
            v-if="getStatusData(statusConfig.status)"
            class="today-stat-card-inner"
            :class="statusConfig.class"
          >
            <div class="today-card-header">
              <div class="card-left">
                <div class="card-icon-wrapper">
                  <feather-icon
                    :icon="statusConfig.icon"
                    size="28"
                    class="card-icon"
                  />
                </div>
                <div>
                  <h3 class="card-title-inline">
                    {{ statusConfig.title }}
                  </h3>
                  <p class="today-card-description">
                    {{ statusConfig.description }}
                  </p>
                </div>
              </div>
              <span class="today-badge">
                Aujourd'hui
              </span>
            </div>
            <div class="today-stats-row">
              <div class="today-stat-block">
                <small>N° colis</small>
                <h2>
                  {{ formatNumber(getStatusData(statusConfig.status).number) }}
                </h2>
              </div>
              <div class="today-stat-separator" />
              <div class="today-stat-block">
                <small>Montant</small>
                <p>
                  {{ formatCurrency(getStatusData(statusConfig.status).total_price_ttc) }}
                </p>
              </div>
            </div>

            <div class="card-footer">
              <div class="progress-bar">
                <div
                  class="progress-fill"
                  :style="{ width: getProgressWidth(statusConfig.status) + '%' }"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="stats-grid">
        <!-- Status Cards -->
        <div
          v-for="statusConfig in otherStatusConfigs"
          :key="statusConfig.status"
          class="stat-card-container"
          @click="show(statusConfig.status)"
        >
          <div
            v-if="getStatusData(statusConfig.status)"
            class="stat-card"
            :class="statusConfig.class"
          >
            <div class="card-header">
              <div class="card-left">
                <div class="card-icon-wrapper">
                  <feather-icon
                    :icon="statusConfig.icon"
                    size="24"
                    class="card-icon"
                  />
                </div>
                <div class="card-title-header">
                  <h3 class="card-title-inline">
                    {{ statusConfig.title }}
                  </h3>
                </div>
              </div>

            </div>

            <div class="card-content">
              <div class="card-stats">
                <div class="stat-number-row">
                  <p class="stat-label">
                    N° colis :
                  </p>
                  <h2 class="stat-number">
                    {{ formatNumber(getStatusData(statusConfig.status).number) }}
                  </h2>
                </div>
                <div class="stat-amount-row">
                  <p class="stat-label">
                    Prix :
                  </p>
                  <p class="stat-amount">
                    {{ formatCurrency(getStatusData(statusConfig.status).total_price_ttc) }}
                  </p>
                </div>
              </div>
            </div>

            <div class="card-footer">
              <div class="progress-bar">
                <div
                  class="progress-fill"
                  :style="{ width: getProgressWidth(statusConfig.status) + '%' }"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Quick Stats Summary -->
    <div class="summary-section">
      <b-card class="summary-card">
        <div class="summary-content">
          <div class="summary-item">
            <feather-icon
              icon="PackageIcon"
              size="20"
              class="summary-icon"
            />
            <div class="summary-data">
              <h4 class="summary-number">
                {{ getTotalOrders() }}
              </h4>
              <p class="summary-label">
                Total Commandes
              </p>
            </div>
          </div>

          <div class="summary-divider" />

          <div class="summary-item">
            <feather-icon
              icon="DollarSignIcon"
              size="20"
              class="summary-icon success"
            />
            <div class="summary-data">
              <h4 class="summary-number">
                {{ formatCurrency(getTotalRevenue()) }}
              </h4>
              <p class="summary-label">
                Chiffre d'Affaires
              </p>
            </div>
          </div>

          <div class="summary-divider" />

          <div class="summary-item">
            <feather-icon
              icon="TrendingUpIcon"
              size="20"
              class="summary-icon warning"
            />
            <div class="summary-data">
              <h4 class="summary-number">
                {{ getDeliveryRate() }}%
              </h4>
              <p class="summary-label">
                Taux de Livraison
              </p>
            </div>
          </div>

          <div class="summary-divider" />

          <div class="summary-item">
            <feather-icon
              icon="TrendingDownIcon"
              size="20"
              class="summary-icon error"
            />
            <div class="summary-data">
              <h4 class="summary-number">
                {{ getReturnRate() }}%
              </h4>
              <p class="summary-label">
                Taux de Retour
              </p>
            </div>
          </div>

          <div class="summary-divider" />

          <div class="summary-item">
            <feather-icon
              icon="ClockIcon"
              size="20"
              class="summary-icon info"
            />
            <div class="summary-data">
              <h4 class="summary-number">
                {{ getPendingOrders() }}
              </h4>
              <p class="summary-label">
                En Attente
              </p>
            </div>
          </div>
        </div>
      </b-card>
    </div>
  </div>
</template>

<script>
import {
  BCard,
  BButton,
} from 'bootstrap-vue'
import axios from 'axios'
import storeAuth from '@/store/store'

export default {
  name: 'Dashboard',
  components: {
    BCard,
    BButton,
  },
  data() {
    return {
      result: [],
      isLoading: false,
      user: {},
      currentTime: new Date(),
      statusConfigs: [
        {
          status: 0,
          title: 'En Attente',
          description: 'Commandes créées',
          icon: 'ClockIcon',
          trendIcon: 'TrendingUpIcon',
          trendClass: 'trend-neutral',
          class: 'pending-status',
        },
        {
          status: 15,
          title: 'Attente Collecte',
          description: 'En attente pickup',
          icon: 'PackageIcon',
          trendIcon: 'ClockIcon',
          trendClass: 'trend-neutral',
          class: 'attente-collecte-status',
        },
        {
          status: 1,
          title: 'Collecté',
          description: 'Colis récupérés',
          icon: 'CheckCircleIcon',
          trendIcon: 'ArrowUpIcon',
          trendClass: 'trend-positive',
          class: 'collected-status',
        },
        {
          status: 2,
          title: 'Au Dépôt',
          description: 'Colis au dépôt',
          icon: 'HomeIcon',
          trendIcon: 'DownloadIcon',
          trendClass: 'trend-neutral',
          class: 'depot-status',
        },
        {
          status: 3,
          title: 'En Cours',
          description: 'Colis en cours',
          icon: 'TruckIcon',
          trendIcon: 'NavigationIcon',
          trendClass: 'trend-active',
          class: 'en-cours-status',
        },
        {
          status: 4,
          title: 'Livré',
          description: 'Livraison réussie',
          icon: 'CheckSquareIcon',
          trendIcon: 'CheckIcon',
          trendClass: 'trend-positive',
          class: 'delivered-status',
        },
        {
          status: 5,
          title: 'Retour Au Dépôt',
          description: 'Retour au dépôt',
          icon: 'RotateCcwIcon',
          trendIcon: 'AlertTriangleIcon',
          trendClass: 'trend-warning',
          class: 'return-status',
        },
        {
          status: 6,
          title: 'Livré & Payé',
          description: 'Processus terminé',
          icon: 'CreditCardIcon',
          trendIcon: 'DollarSignIcon',
          trendClass: 'trend-positive',
          class: 'completed-status',
        },
        {
          status: 7,
          title: 'Retour Définitif',
          description: 'Retour fournisseur',
          icon: 'XCircleIcon',
          trendIcon: 'AlertCircleIcon',
          trendClass: 'trend-negative',
          class: 'final-return-status',
        },
        {
          status: 19,
          title: 'Livré Temporaire',
          description: 'En attente de validation dépôt',
          icon: 'CheckCircleIcon',
          trendIcon: 'ClockIcon',
          trendClass: 'trend-warning',
          class: 'livre-temporaire-status',
        },
        {
          status: 20,
          title: 'Retour Temporaire',
          description: 'À revérifier au dépôt',
          icon: 'RotateCwIcon',
          trendIcon: 'ClockIcon',
          trendClass: 'trend-warning',
          class: 'retour-temporaire-status',
        },
        {
          status: 8,
          title: 'En Transfert',
          description: 'Transfert en cours',
          icon: 'RefreshCwIcon',
          trendIcon: 'ArrowRightCircleIcon',
          trendClass: 'trend-active',
          class: 'transfer-status',
        },
        {
          status: 9,
          title: 'Retour Payé',
          description: 'Paiement retour',
          icon: 'FileTextIcon',
          trendIcon: 'DollarSignIcon',
          trendClass: 'trend-warning',
          class: 'return-billed-status',
        },
        // {
        //   status: 12,
        //   title: 'Annulé',
        //   description: 'Commande annulée',
        //   icon: 'XIcon',
        //   trendIcon: 'StopCircleIcon',
        //   trendClass: 'trend-negative',
        //   class: 'cancelled-status',
        // },
        // {
        //   status: 13,
        //   title: 'Colis Perdu',
        //   description: 'Colis introuvable',
        //   icon: 'AlertTriangleIcon',
        //   trendIcon: 'SearchIcon',
        //   trendClass: 'trend-negative',
        //   class: 'lost-status',
        // },
        // {
        //   status: 14,
        //   title: 'À Vérifier',
        //   description: 'Vérification requise',
        //   icon: 'HelpCircleIcon',
        //   trendIcon: 'EyeIcon',
        //   trendClass: 'trend-warning',
        //   class: 'verify-status',
        // },
        {
          status: 18,
          title: 'Rendez Vous',
          description: 'Rendez Vous',
          icon: 'CalendarIcon',
          trendIcon: 'ClockIcon',
          trendClass: 'trend-neutral',
          class: 'planning-status',
        },
        {
          status: 16,
          title: 'À Rembourser',
          description: 'En attente de remboursement',
          icon: 'DollarSignIcon',
          trendIcon: 'ClockIcon',
          trendClass: 'trend-warning',
          class: 'a-rembourser-status',
        },
        {
          status: 17,
          title: 'Remboursé',
          description: 'Remboursement effectué',
          icon: 'CheckCircleIcon',
          trendIcon: 'CheckIcon',
          trendClass: 'trend-positive',
          class: 'rembourse-status',
        },
      ],
    }
  },
  computed: {
    todayStatusConfigs() {
      const todayStatuses = [19, 20]
      return this.statusConfigs.filter(cfg => todayStatuses.includes(cfg.status))
    },
    otherStatusConfigs() {
      const todayStatuses = new Set([19, 20])
      return this.statusConfigs.filter(cfg => !todayStatuses.has(cfg.status))
    },
    statusItems() {
      if (!this.result || this.result.length === 0) {
        return []
      }

      return [
        {
          title: 'En attente de collecte',
          subtitle: 'Expéditions en attente',
          count: this.result.find(item => item.status === 0)?.number || 0,
          color: 'warning',
          icon: 'ClockIcon',
          status: 0,
        },
        {
          title: 'Collectées',
          subtitle: 'Expéditions collectées',
          count: this.result.find(item => item.status === 1)?.number || 0,
          color: 'info',
          icon: 'TruckIcon',
          status: 1,
        },
        {
          title: 'En transit',
          subtitle: 'Centre de tri',
          count: this.result.find(item => item.status === 2)?.number || 0,
          color: 'primary',
          icon: 'PackageIcon',
          status: 2,
        },
        {
          title: 'En livraison',
          subtitle: 'Livraison en cours',
          count: this.result.find(item => item.status === 3)?.number || 0,
          color: 'info',
          icon: 'TruckIcon',
          status: 3,
        },
        {
          title: 'Livrées avec succès',
          subtitle: 'Livraisons réussies',
          count: this.result.find(item => item.status === 4)?.number || 0,
          color: 'success',
          icon: 'CheckCircleIcon',
          status: 4,
        },
        {
          title: 'Retour centre',
          subtitle: 'Échec de livraison',
          count: this.result.find(item => item.status === 5)?.number || 0,
          color: 'warning',
          icon: 'RotateCcwIcon',
          status: 5,
        },
        {
          title: 'Confirmées et payées',
          subtitle: 'Livraisons payées',
          count: this.result.find(item => item.status === 6)?.number || 0,
          color: 'success',
          icon: 'DollarSignIcon',
          status: 6,
        },
        {
          title: 'Retour définitif',
          subtitle: 'Retour expéditeur',
          count: this.result.find(item => item.status === 7)?.number || 0,
          color: 'danger',
          icon: 'ArrowLeftIcon',
          status: 7,
        },
        {
          title: 'Livré Temporaire',
          subtitle: 'Validations dépôt en attente',
          count: this.result.find(item => item.status === 19)?.number || 0,
          color: 'success',
          icon: 'CheckCircleIcon',
          status: 19,
        },
        {
          title: 'Retour Temporaire',
          subtitle: 'Suivi fin de journée',
          count: this.result.find(item => item.status === 20)?.number || 0,
          color: 'warning',
          icon: 'RotateCwIcon',
          status: 20,
        },
        {
          title: 'Transfert inter-centres',
          subtitle: 'En transfert',
          count: this.result.find(item => item.status === 8)?.number || 0,
          color: 'secondary',
          icon: 'ArrowRightIcon',
          status: 8,
        },
        {
          title: 'Retours payés',
          subtitle: 'Retours payés',
          count: this.result.find(item => item.status === 9)?.number || 0,
          color: 'danger',
          icon: 'DollarSignIcon',
          status: 9,
        },
        {
          title: 'Prêtes pour collecte',
          subtitle: 'Disponibles collecte',
          count: this.result.find(item => item.status === 15)?.number || 0,
          color: 'info',
          icon: 'ClockIcon',
          status: 15,
        },
        {
          title: 'Nécessitent vérification',
          subtitle: 'À vérifier',
          count: this.result.find(item => item.status === 14)?.number || 0,
          color: 'warning',
          icon: 'AlertTriangleIcon',
          status: 14,
        },
        {
          title: 'Rendez Vous',
          subtitle: 'Rendez Vous',
          count: this.result.find(item => item.status === 18)?.number || 0,
          color: 'info',
          icon: 'CalendarIcon',
          status: 18,
        },
        {
          title: 'À Rembourser',
          subtitle: 'En attente de remboursement',
          count: this.result.find(item => item.status === 16)?.number || 0,
          color: 'warning',
          icon: 'DollarSignIcon',
          status: 16,
        },
        {
          title: 'Remboursé',
          subtitle: 'Remboursement effectué',
          count: this.result.find(item => item.status === 17)?.number || 0,
          color: 'info',
          icon: 'CheckCircleIcon',
          status: 17,
        },
      ] // Show all statuses (temporarily for testing)
      // .filter(item => item.count > 0) // Only show statuses that have orders
    },
  },
  created() {
    this.user = storeAuth.state.user
    // Apply default filter on load
    this.loadInitialData()
  },
  mounted() {
    this.updateTime()
    setInterval(this.updateTime, 1000)
  },
  methods: {
    async loadInitialData() {
      try {
        await this.getOrders()
      } catch (error) {
        console.error('Error loading initial data:', error)
      }
    },

    show(level) {
      this.$router.push({
        name: 'order-list',
        params: { level: level.toString() },
      })
    },

    getStatusData(status) {
      return this.result.find(item => item.status === status)
    },

    async getOrders() {
      this.isLoading = true
      let supplier = null
      if (this.user.role === 'supplier') {
        supplier = this.user.id
      }

      // Build query params for GET request
      const params = {}
      if (supplier) params.supplier = supplier
      if (this.user.repository) params.repository = this.user.repository

      try {
        const response = await axios.get('/api/orders/dashboard/', { params })
        this.result = response.data
      } catch (error) {
        console.error('Error fetching dashboard data:', error)
        this.$toast({
          component: 'ToastificationContent',
          props: {
            title: 'Erreur',
            text: 'Erreur lors du chargement des données',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.isLoading = false
      }
    },

    refreshData() {
      this.getOrders()
    },

    updateTime() {
      this.currentTime = new Date()
    },

    getCurrentTime() {
      return this.currentTime.toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    },

    formatNumber(num) {
      return new Intl.NumberFormat('fr-FR').format(num || 0)
    },

    formatCurrency(amount) {
      // Handle NaN, null, undefined, and non-numeric values
      const numericAmount = parseFloat(amount) || 0
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'TND',
        minimumFractionDigits: 0,
      }).format(numericAmount)
    },

    getTotalOrders() {
      return this.result.reduce((sum, item) => sum + (item.number || 0), 0)
    },

    getTotalRevenue() {
      return this.result.reduce((sum, item) => {
        const price = parseFloat(item.total_price_ttc) || 0
        return sum + price
      }, 0)
    },

    getDeliveryRate() {
      const delivered = this.getStatusData(4)?.number || 0
      const total = this.getTotalOrders()
      return total > 0 ? Math.round((delivered / total) * 100) : 0
    },

    getReturnRate() {
      // Calculate return rate based on returned orders (status 5, 7, 9)
      const returnedToDepot = this.getStatusData(5)?.number || 0 // Retour Centre
      const returnedToSupplier = this.getStatusData(7)?.number || 0 // Retour Définitif
      const returnedBilled = this.getStatusData(9)?.number || 0 // Retour Payé

      const totalReturned = returnedToDepot + returnedToSupplier + returnedBilled
      const total = this.getTotalOrders()
      return total > 0 ? Math.round((totalReturned / total) * 100) : 0
    },

    getPendingOrders() {
      return (this.getStatusData(0)?.number || 0) + (this.getStatusData(1)?.number || 0)
    },

    getProgressWidth(status) {
      const data = this.getStatusData(status)
      if (!data) return 0
      const total = this.getTotalOrders()
      return total > 0 ? Math.min((data.number / total) * 100, 100) : 0
    },
  },
}
</script>

<style lang="scss" scoped>
.modern-dashboard {
  min-height: 100vh;
  background: #ffffff;
  font-family: 'Inter', 'Segoe UI', sans-serif;
}

// Header Section
.dashboard-header {
  background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
  color: white;
  padding: 2rem 0;
  box-shadow: 0 10px 30px rgba(220, 53, 69, 0.3);

  .header-content {
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 2rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .brand-section {
    display: flex;
    align-items: center;
    gap: 1.5rem;

    .brand-icon {
      width: 60px;
      height: 60px;
      background: rgba(255, 255, 255, 0.15);
      border-radius: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      backdrop-filter: blur(10px);

      .brand-logo {
        color: white;
      }
    }

    .brand-info {
      .brand-title {
        font-size: 2rem;
        font-weight: 700;
        margin: 0;
        text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
      }

      .brand-subtitle {
        margin: 0;
        opacity: 0.9;
        font-size: 1.1rem;
      }
    }
  }

    }

  .info-notice-container {
    max-width: 1400px;
    margin: 2rem auto;
    padding: 0 2rem;

    .info-notice {
      background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
      border-radius: 20px;
      padding: 2rem;
      box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
      border: 1px solid #e2e8f0;
      display: flex;
      align-items: flex-start;
      gap: 1.5rem;
      position: relative;
      overflow: hidden;
      backdrop-filter: blur(10px);

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 4px;
        background: linear-gradient(90deg, #dc3545 0%, #fd7e14 50%, #20c997 100%);
      }

      .notice-icon {
        width: 50px;
        height: 50px;
        background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
        border-radius: 15px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        box-shadow: 0 8px 25px rgba(220, 53, 69, 0.3);

        .icon {
          color: white;
        }
      }

      .notice-content {
        flex: 1;
        text-align: right;
        direction: rtl;

        .notice-text {
          margin: 0 0 1rem;
          font-size: 1.3rem;
          line-height: 1.8;
          color: #334155;
          font-weight: 600;
          letter-spacing: 0.3px;
        }

        .notice-tagline {
          margin: 0;
          font-size: 1.4rem;
          font-weight: 700;
          color: #dc3545;
          padding: 0.5rem 1rem;
          background: rgba(220, 53, 69, 0.1);
          border-radius: 10px;
          border-right: 4px solid #dc3545;
          text-align: center;
        }
      }

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 15px 50px rgba(0, 0, 0, 0.12);
        transition: all 0.3s ease;
      }
    }
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: 2rem;

    .time-display {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      background: rgba(255, 255, 255, 0.1);
      padding: 0.5rem 1rem;
      border-radius: 12px;
      backdrop-filter: blur(10px);

      .current-time {
        font-weight: 600;
        font-family: 'Monaco', monospace;
      }
    }

    .refresh-btn {
      background: rgba(255, 255, 255, 0.15);
      border: 1px solid rgba(255, 255, 255, 0.2);
      color: white;
      padding: 0.75rem 1.5rem;
      border-radius: 12px;
      backdrop-filter: blur(10px);
      transition: all 0.3s ease;

      &:hover {
        background: rgba(255, 255, 255, 0.25);
        transform: translateY(-2px);
      }

      .spinning {
        animation: spin 1s linear infinite;
      }
    }
  }

// Holiday Banner
.holiday-banner {
  max-width: 1400px;
  margin: 2rem auto 2rem; // Changed from 0 auto 2rem to add top margin
  padding: 0 2rem;

  .holiday-content {
    background: linear-gradient(135deg, #fd7e14 0%, #e67e22 100%);
    border-radius: 20px;
    padding: 1.5rem 2rem;
    display: flex;
    align-items: center;
    gap: 1.5rem;
    color: white;
    position: relative;
    overflow: hidden;

    .holiday-icon {
      font-size: 2.5rem;
      animation: bounce 2s infinite;
    }

    .holiday-text {
      flex: 1;
      text-align: center;
      direction: rtl;

      .holiday-title {
        margin: 0 0 0.5rem;
        font-size: 1.5rem;
        font-weight: 700;
      }

      .holiday-message {
        margin: 0;
        opacity: 0.95;
      }
    }

    .holiday-decoration {
      position: absolute;
      top: 0;
      right: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;

      .sparkle {
        position: absolute;
        width: 4px;
        height: 4px;
        background: white;
        border-radius: 50%;
        animation: sparkle 3s infinite;

        &:nth-child(1) {
          top: 20%;
          right: 20%;
          animation-delay: 0s;
        }

        &:nth-child(2) {
          top: 60%;
          right: 80%;
          animation-delay: 1s;
        }

        &:nth-child(3) {
          top: 80%;
          right: 40%;
          animation-delay: 2s;
        }
      }
    }
  }
}

// Loading Overlay
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(5px);

  .loading-content {
    text-align: center;

    .loading-spinner {
      width: 60px;
      height: 60px;
      border: 4px solid #f3f3f3;
      border-top: 4px solid #dc3545;
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin: 0 auto 1rem;
    }

    .loading-text {
      color: #6c757d;
      font-weight: 500;
      font-size: 1.1rem;
    }
  }
}

// Statistics Grid
.stats-container {
  max-width: 1400px;
  margin: 0 auto 2rem;
  padding: 0 2rem;

  .today-stats-grid {
    margin-bottom: 2rem;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
    gap: 1.5rem;
  }

  .today-stat-card {
    cursor: pointer;
    transition: transform 0.3s ease;

    &:hover {
      transform: translateY(-6px);
    }
  }

  .today-stat-card-inner {
    border-radius: 24px;
    padding: 1.75rem;
    color: var(--card-text-color, #fff);
    position: relative;
    overflow: hidden;
    background: var(--card-bg, linear-gradient(135deg, #1f2937 0%, #111827 100%));
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.2);

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: inherit;
      border: 1px solid rgba(255, 255, 255, 0.12);
      pointer-events: none;
    }

    .card-footer {
      margin-top: 1.5rem;
      display: block;

      .progress-bar {
        height: 6px;
        background: rgba(255, 255, 255, 0.15);
        border-radius: 999px;
        overflow: hidden;

        .progress-fill {
          height: 100%;
          background: var(--progress-color, rgba(255, 255, 255, 0.4));
          border-radius: 999px;
          transition: width 0.6s ease;
        }
      }
    }
  }

  .today-card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
  }

  .today-card-description {
    margin: 0;
    color: rgba(255, 255, 255, 0.8);
    font-size: 0.95rem;
  }

  .today-badge {
    background: rgba(255, 255, 255, 0.15);
    padding: 0.25rem 0.75rem;
    border-radius: 999px;
    font-size: 0.85rem;
    font-weight: 600;
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }

  .today-stats-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1.25rem;
  }

  .today-stat-block {
    flex: 1;

    small {
      display: block;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      font-weight: 600;
      color: rgba(255, 255, 255, 0.7);
      margin-bottom: 0.25rem;
    }

    h2 {
      margin: 0;
      font-size: 2.75rem;
      font-weight: 800;
    }

    p {
      margin: 0;
      font-size: 1.25rem;
      font-weight: 700;
    }
  }

  .today-stat-separator {
    width: 1px;
    height: 64px;
    background: rgba(255, 255, 255, 0.2);
  }

  .stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
    gap: 1.5rem;

    .stat-card-container {
      cursor: pointer;
      transition: transform 0.3s ease;

      &:hover {
        transform: translateY(-8px);
      }

      .stat-card {
        background: var(--card-bg, white);
        border-radius: 20px;
        padding: 1.5rem;
        box-shadow: 0 5px 30px rgba(0, 0, 0, 0.03);
        border: 1px solid #f1f5f9;
        transition: all 0.3s ease;
        height: 100%;
        color: var(--card-text-color, #1f2937);

        &:hover {
          box-shadow: 0 10px 50px rgba(0, 0, 0, 0.06);
        }

        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;

          .card-left {
            display: flex;
            align-items: center;
            gap: 1rem;
          }

          .card-icon-wrapper {
            width: 50px;
            height: 50px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: var(--icon-bg);

            .card-icon {
              color: var(--icon-color);
            }
          }

          .card-title-header {
            .card-title-inline {
              font-size: 1.4rem;
              font-weight: 700;
              color: #000000;
              margin: 0;
            }
          }
        }

        .card-content {
          margin-bottom: 1rem;

          .card-stats {
            margin-bottom: 0.75rem;

            .stat-number-row {
              display: flex;
              align-items: baseline;
              gap: 0.5rem;
              margin-bottom: 1.5rem;
            }

            .stat-amount-row {
              display: flex;
              align-items: baseline;
              gap: 0.5rem;
            }

            .stat-label {
              color: var(--card-text-secondary, #6b7280);
              font-size: 1.75rem;
              font-weight: 900;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              margin: 0;
            }

            .stat-number {
              font-size: 2rem;
              font-weight: 700;
              color: var(--card-text-color, #1f2937);
              margin: 0;
              line-height: 1;
            }

            .stat-amount {
              color: var(--card-text-secondary, #6b7280);
              font-weight: 900;
              margin: 0;
              font-size: 1.75rem;
            }
          }

          .card-info {
            .card-title {
              font-size: 1.1rem;
              font-weight: 600;
              color: var(--card-text-color, #374151);
              margin: 0 0 0.25rem;
            }

            .card-description {
              color: var(--card-text-secondary, #9ca3af);
              font-size: 0.85rem;
              margin: 0;
            }
          }
        }

        .card-footer {
          display: none;
        }
      }
    }
  }
}

// Status-specific styles
.pending-status {
  --card-bg: #FFCDAA;
  --card-text-color: #000000;
  --card-text-secondary: #000000;
  --icon-bg: rgba(255, 255, 255, 0.1);
  --icon-color: white;
  --progress-color: rgba(255, 255, 255, 0.4);
}

.attente-collecte-status {
  --card-bg: #f5e6a3;
  --card-text-color: #000000;
  --card-text-secondary: #000000;
  --icon-bg: rgba(90, 74, 58, 0.15);
  --icon-color: #5a4a3a;
  --progress-color: rgba(90, 74, 58, 0.6);
}

.waiting-status {
  --card-bg: #28a745;
  --card-text-color: #000000;
  --card-text-secondary: #000000;
  --icon-bg: rgba(255, 255, 255, 0.2);
  --icon-color: white;
  --trend-bg: rgba(255, 255, 255, 0.2);
  --progress-color: rgba(255, 255, 255, 0.8);
}

.collected-status {
  --card-bg: #F5DF9C;
  --card-text-color: #000000;
  --card-text-secondary: #000000;
  --icon-bg: rgba(255, 255, 255, 0.2);
  --icon-color: white;
  --trend-bg: rgba(255, 255, 255, 0.2);
  --progress-color: rgba(255, 255, 255, 0.8);
}

.depot-status {
  --card-bg: #8FDCE8;
  --card-text-color: #000000;
  --card-text-secondary: #000000;
  --icon-bg: rgba(255, 255, 255, 0.2);
  --icon-color: white;
  --trend-bg: rgba(255, 255, 255, 0.2);
  --progress-color: rgba(255, 255, 255, 0.8);
}

.en-cours-status {
  --card-bg: #89F8A8;
  --card-text-color: #000000;
  --card-text-secondary: #000000;
  --icon-bg: rgba(255, 255, 255, 0.2);
  --icon-color: white;
  --trend-bg: rgba(255, 255, 255, 0.2);
  --progress-color: rgba(255, 255, 255, 0.8);
}

.delivery-status {
  --card-bg: #fd7e14;
  --card-text-color: #000000;
  --card-text-secondary: #000000;
  --icon-bg: rgba(255, 255, 255, 0.2);
  --icon-color: white;
  --trend-bg: rgba(255, 255, 255, 0.2);
  --progress-color: rgba(255, 255, 255, 0.8);
}

.delivered-status {
  --card-bg: #4DB96C;
  --card-text-color: #000000;
  --card-text-secondary: #000000;
  --icon-bg: rgba(255, 255, 255, 0.2);
  --icon-color: white;
  --trend-bg: rgba(255, 255, 255, 0.2);
  --progress-color: rgba(255, 255, 255, 0.8);
}

.return-status {
  --card-bg: #E9503F;
  --card-text-color: #000000;
  --card-text-secondary: #000000;
  --icon-bg: rgba(255, 255, 255, 0.2);
  --icon-color: white;
  --trend-bg: rgba(255, 255, 255, 0.2);
  --progress-color: rgba(255, 255, 255, 0.8);
}

.completed-status {
  --card-bg: #20c997;
  --card-text-color: #000000;
  --card-text-secondary: #000000;
  --icon-bg: rgba(255, 255, 255, 0.2);
  --icon-color: white;
  --trend-bg: rgba(255, 255, 255, 0.2);
  --progress-color: rgba(255, 255, 255, 0.8);
}

.final-return-status {
  --card-bg: #B82818;
  --card-text-color: #000000;
  --card-text-secondary: #000000;
  --icon-bg: rgba(255, 255, 255, 0.2);
  --icon-color: white;
  --trend-bg: rgba(255, 255, 255, 0.2);
  --progress-color: rgba(255, 255, 255, 0.8);
}

.transfer-status {
  --card-bg: #CB90BC;
  --card-text-color: #000000;
  --card-text-secondary: #000000;
  --icon-bg: rgba(255, 255, 255, 0.2);
  --icon-color: white;
  --trend-bg: rgba(255, 255, 255, 0.2);
  --progress-color: rgba(255, 255, 255, 0.8);
}

.return-billed-status {
  --card-bg: #E27F66;
  --card-text-color: #000000;
  --card-text-secondary: #000000;
  --icon-bg: rgba(255, 255, 255, 0.2);
  --icon-color: white;
  --trend-bg: rgba(255, 255, 255, 0.2);
  --progress-color: rgba(255, 255, 255, 0.8);
}

.inter-transfer-status {
  --card-bg: #8b4513;
  --card-text-color: #000000;
  --card-text-secondary: #000000;
  --icon-bg: rgba(255, 255, 255, 0.2);
  --icon-color: white;
  --trend-bg: rgba(255, 255, 255, 0.2);
  --progress-color: rgba(255, 255, 255, 0.8);
}

.received-status {
  --card-bg: #e91e63;
  --card-text-color: #000000;
  --card-text-secondary: #000000;
  --icon-bg: rgba(255, 255, 255, 0.2);
  --icon-color: white;
  --trend-bg: rgba(255, 255, 255, 0.2);
  --progress-color: rgba(255, 255, 255, 0.8);
}

.cancelled-status {
  --card-bg: #AAA3A3;
  --card-text-color: #000000;
  --card-text-secondary: #000000;
  --icon-bg: rgba(255, 255, 255, 0.2);
  --icon-color: white;
  --trend-bg: rgba(255, 255, 255, 0.2);
  --progress-color: rgba(255, 255, 255, 0.8);
}

.lost-status {
  --card-bg: #C43737;
  --card-text-color: #000000;
  --card-text-secondary: #000000;
  --icon-bg: rgba(255, 255, 255, 0.2);
  --icon-color: white;
  --trend-bg: rgba(255, 255, 255, 0.2);
  --progress-color: rgba(255, 255, 255, 0.8);
}

.verify-status {
  --card-bg: #EDEF72;
  --card-text-color: #000000;
  --card-text-secondary: #000000;
  --icon-bg: rgba(33, 37, 41, 0.1);
  --icon-color: #212529;
  --trend-bg: rgba(33, 37, 41, 0.1);
  --progress-color: rgba(33, 37, 41, 0.8);
}

.planning-status {
  --card-bg: rgba(183, 184, 252, 0.91);
  --card-text-color: #000000;
  --card-text-secondary: #000000;
  --icon-bg: rgba(255, 255, 255, 0.2);
  --icon-color: white;
  --trend-bg: rgba(255, 255, 255, 0.2);
  --progress-color: rgba(255, 255, 255, 0.8);
}

.a-rembourser-status {
  --card-bg: rgba(78, 131, 164, 0.62);
  --card-text-color: #000000;
  --card-text-secondary: #000000;
  --icon-bg: rgba(255, 255, 255, 0.2);
  --icon-color: white;
  --trend-bg: rgba(255, 255, 255, 0.2);
  --progress-color: rgba(255, 255, 255, 0.8);
}

.rembourse-status {
  --card-bg: rgba(148, 193, 220, 0.49);
  --card-text-color: #000000;
  --card-text-secondary: #000000;
  --icon-bg: rgba(255, 255, 255, 0.2);
  --icon-color: white;
  --trend-bg: rgba(255, 255, 255, 0.2);
  --progress-color: rgba(255, 255, 255, 0.8);
}

.livre-temporaire-status {
  --card-bg: #2EB293;
  --card-text-color: #ffffff;
  --card-text-secondary: rgba(255, 255, 255, 0.9);
  --icon-bg: rgba(255, 255, 255, 0.2);
  --icon-color: white;
  --progress-color: rgba(255, 255, 255, 0.5);
}

.retour-temporaire-status {
  --card-bg: #B53C3C;
  --card-text-color: #ffffff;
  --card-text-secondary: rgba(255, 255, 255, 0.9);
  --icon-bg: rgba(255, 255, 255, 0.2);
  --icon-color: white;
  --progress-color: rgba(255, 255, 255, 0.5);
}

// Summary Section
.summary-section {
  max-width: 1400px;
  margin: 0 auto 2rem;
  padding: 0 2rem;

  .summary-card {
    border: none;
    border-radius: 20px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);

    .summary-content {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .summary-item {
        display: flex;
        align-items: center;
        gap: 0.75rem;

        .summary-icon {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(220, 53, 69, 0.1);
          color: #dc3545;

          &.success {
            background: rgba(16, 185, 129, 0.1);
            color: #10b981;
          }

          &.warning {
            background: rgba(245, 158, 11, 0.1);
            color: #f59e0b;
          }

          &.info {
            background: rgba(59, 130, 246, 0.1);
            color: #3b82f6;
          }

          &.error {
            background: rgba(239, 68, 68, 0.1);
            color: #ef4444;
          }
        }

        .summary-data {
          .summary-number {
            font-size: 1.5rem;
            font-weight: 700;
            color: #1f2937;
            margin: 0;
            line-height: 1;
          }

          .summary-label {
            color: #6b7280;
            font-size: 0.85rem;
            margin: 0;
            font-weight: 500;
          }
        }
      }

      .summary-divider {
        width: 1px;
        height: 40px;
        background: #e5e7eb;
      }
    }
  }
}

// Animations
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-10px); }
  60% { transform: translateY(-5px); }
}

@keyframes sparkle {
  0%, 100% { opacity: 0; transform: scale(0); }
  50% { opacity: 1; transform: scale(1); }
}

// Responsive Design
@media (max-width: 1200px) {
  .stats-grid {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)) !important;
  }
}

@media (max-width: 768px) {
  .dashboard-header .header-content {
    flex-direction: column;
    gap: 1.5rem;
    text-align: center;
  }

  .stats-grid {
    grid-template-columns: 1fr !important;
  }

  .summary-content {
    flex-direction: column;
    gap: 1.5rem;

    .summary-divider {
      width: 100%;
      height: 1px;
    }
  }

  .holiday-content {
    flex-direction: column;
    text-align: center;

    .holiday-text {
      direction: rtl;
    }
  }
}

@media (max-width: 480px) {
  .modern-dashboard {
    padding: 0;
  }

  .dashboard-header,
  .stats-container,
  .summary-section {
    padding: 0 1rem;
  }
}
</style>
